import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  Alert,
  Dimensions,
} from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '../store';
import {
  performIntelligentSearch,
  fetchPersonalizedCategories,
  fetchSkillGapRecommendations,
  updateAdaptiveFilters,
  setQuery,
  clearResults,
  selectIntelligentSearchQuery,
  selectIntelligentSearchResults,
  selectPersonalizedCategories,
  selectSkillGapRecommendations,
  selectAdaptiveFilters,
  selectMasteryBasedFilters,
  selectIntelligentSearchLoading,
  selectIntelligentSearchError,
} from '../store/slices/intelligentSearchSlice';
import { selectActiveRecommendations } from '../store/slices/recommendationEngineSlice';
import { selectMasteryStates } from '../store/slices/aiProfileSlice';
import {
  IntelligentSearchBar,
  PersonalizedCategoryGrid,
  SkillGapRecommendations,
  DifficultyAdaptiveFilter,
} from '../components/ai';
import type {
  IntelligentSuggestion,
  PersonalizedCategory,
  SkillGapRecommendation,
  CategoryFeedback,
  SkillGapFeedback,
  MasteryBasedFilter,
  MasteryFilterFeedback,
  IntelligentSearchResult,
} from '@coffeeplexity/shared';
import { theme } from '../theme';
import { logger } from '../utils/logger';

interface SearchResultsProps {
  results: IntelligentSearchResult[];
  onResultPress: (result: IntelligentSearchResult) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  onResultPress,
}) => {
  if (results.length === 0) return null;

  return (
    <View style={styles.searchResults}>
      {/* Search results would be implemented here */}
      {/* This is a placeholder for the actual search results display */}
    </View>
  );
};

export const IntelligentSearchScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectIntelligentSearchQuery);
  const searchResults = useAppSelector(selectIntelligentSearchResults);
  const personalizedCategories = useAppSelector(selectPersonalizedCategories);
  const skillGapRecommendations = useAppSelector(selectSkillGapRecommendations);
  const adaptiveFilters = useAppSelector(selectAdaptiveFilters);
  const masteryBasedFilters = useAppSelector(selectMasteryBasedFilters);
  const loading = useAppSelector(selectIntelligentSearchLoading);
  const error = useAppSelector(selectIntelligentSearchError);
  const masteryStates = useAppSelector(selectMasteryStates);

  const [activeTab, setActiveTab] = useState<'discover' | 'search' | 'filters'>(
    'discover'
  );
  const [refreshing, setRefreshing] = useState(false);

  // Initialize data on mount
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = useCallback(async () => {
    try {
      const userId = 'current-user'; // TODO: Get from auth state

      // Fetch personalized categories
      dispatch(
        fetchPersonalizedCategories({
          userId,
          masteryStates,
          maxCategories: 8,
        })
      );

      // Fetch skill gap recommendations
      dispatch(
        fetchSkillGapRecommendations({
          userId,
          masteryStates,
          maxRecommendations: 5,
        })
      );

      // Update adaptive filters
      dispatch(
        updateAdaptiveFilters({
          userId,
          masteryStates,
        })
      );
    } catch (error) {
      logger.error('Failed to initialize intelligent search data:', error);
    }
  }, [dispatch, masteryStates]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await initializeData();
    setRefreshing(false);
  }, [initializeData]);

  const handleSearch = useCallback(
    (searchQuery: string) => {
      dispatch(setQuery(searchQuery));
      dispatch(
        performIntelligentSearch({
          query: searchQuery,
          userId: 'current-user', // TODO: Get from auth state
          context: {
            currentSkills: masteryStates,
            recentActivity: [],
            sessionContext: { screen: 'intelligent_search' },
            deviceContext: { platform: 'mobile' },
            timeContext: { timestamp: new Date().toISOString() },
          },
          includePersonalizedCategories: true,
          includeSkillGapRecommendations: true,
          adaptiveFiltering: true,
        })
      );
      setActiveTab('search');
    },
    [dispatch, masteryStates]
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: IntelligentSuggestion) => {
      handleSearch(suggestion.text);
    },
    [handleSearch]
  );

  const handleCategorySelect = useCallback(
    (category: PersonalizedCategory) => {
      // Navigate to category content or perform category-based search
      handleSearch(`category:${category.name}`);
    },
    [handleSearch]
  );

  const handleCategoryFeedback = useCallback(
    (categoryId: string, feedback: CategoryFeedback) => {
      // Feedback is handled by the component and slice
      logger.log('Category feedback:', categoryId, feedback);
    },
    []
  );

  const handleSkillGapSelect = useCallback(
    (recommendation: SkillGapRecommendation) => {
      // Navigate to skill gap learning path or show detailed view
      logger.log('Skill gap selected:', recommendation);
    },
    []
  );

  const handleSkillGapFeedback = useCallback(
    (recommendationId: string, feedback: SkillGapFeedback) => {
      // Feedback is handled by the component
      logger.log('Skill gap feedback:', recommendationId, feedback);
    },
    []
  );

  const handleFilterChange = useCallback((filters: MasteryBasedFilter[]) => {
    // Filters are updated and will trigger re-search if needed
    logger.log('Filters updated:', filters);
  }, []);

  const handleFilterFeedback = useCallback(
    (filterId: string, feedback: MasteryFilterFeedback) => {
      // Feedback is handled by the component
      logger.log('Filter feedback:', filterId, feedback);
    },
    []
  );

  const handleResultPress = useCallback((result: IntelligentSearchResult) => {
    // Navigate to content detail screen
    logger.log('Result selected:', result);
  }, []);

  const handleClearSearch = useCallback(() => {
    dispatch(clearResults());
    setActiveTab('discover');
  }, [dispatch]);

  // Show error if there's a critical error
  useEffect(() => {
    if (error) {
      Alert.alert(
        'Search Error',
        'There was an issue with the intelligent search. Please try again.',
        [{ text: 'Retry', onPress: initializeData }, { text: 'OK' }]
      );
    }
  }, [error, initializeData]);

  const renderDiscoverTab = () => (
    <ScrollView
      style={styles.tabContent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
    >
      <View style={styles.section}>
        <PersonalizedCategoryGrid
          categories={personalizedCategories}
          onCategorySelect={handleCategorySelect}
          onCategoryFeedback={handleCategoryFeedback}
          maxCategories={8}
          showSubcategories={true}
        />
      </View>

      <View style={styles.section}>
        <SkillGapRecommendations
          recommendations={skillGapRecommendations}
          onRecommendationSelect={handleSkillGapSelect}
          onFeedback={handleSkillGapFeedback}
          maxRecommendations={5}
          showLearningPath={true}
        />
      </View>
    </ScrollView>
  );

  const renderSearchTab = () => (
    <ScrollView
      style={styles.tabContent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
    >
      <SearchResults
        results={searchResults}
        onResultPress={handleResultPress}
      />
    </ScrollView>
  );

  const renderFiltersTab = () => (
    <ScrollView
      style={styles.tabContent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
    >
      <DifficultyAdaptiveFilter
        masteryStates={masteryStates}
        currentFilters={masteryBasedFilters}
        onFilterChange={handleFilterChange}
        onFeedback={handleFilterFeedback}
        adaptationEnabled={true}
        showExplanations={true}
      />
    </ScrollView>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <View style={styles.header}>
        <IntelligentSearchBar
          onSearch={handleSearch}
          onSuggestionSelect={handleSuggestionSelect}
          placeholder='Search for recipes, techniques, or ask questions...'
          autoFocus={false}
          showSuggestions={true}
          maxSuggestions={5}
          debounceMs={300}
        />
      </View>

      <View style={styles.tabBar}>
        <View style={styles.tabButtons}>
          <View style={styles.tabButton}>
            <View
              style={[
                styles.tabButtonContent,
                activeTab === 'discover' && styles.tabButtonActive,
              ]}
            >
              <Text style={styles.tabButtonText}>Discover</Text>
            </View>
          </View>

          <View style={styles.tabButton}>
            <View
              style={[
                styles.tabButtonContent,
                activeTab === 'search' && styles.tabButtonActive,
              ]}
            >
              <Text style={styles.tabButtonText}>
                Search {searchResults.length > 0 && `(${searchResults.length})`}
              </Text>
            </View>
          </View>

          <View style={styles.tabButton}>
            <View
              style={[
                styles.tabButtonContent,
                activeTab === 'filters' && styles.tabButtonActive,
              ]}
            >
              <Text style={styles.tabButtonText}>Filters</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.content}>
        {activeTab === 'discover' && renderDiscoverTab()}
        {activeTab === 'search' && renderSearchTab()}
        {activeTab === 'filters' && renderFiltersTab()}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tabBar: {
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tabButtons: {
    flexDirection: 'row',
    paddingHorizontal: 16,
  },
  tabButton: {
    flex: 1,
  },
  tabButtonContent: {
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    borderBottomColor: '#2196F3',
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  content: {
    flex: 1,
  },
  tabContent: {
    flex: 1,
  },
  section: {
    marginBottom: 24,
  },
  searchResults: {
    padding: 16,
  },
});

export default IntelligentSearchScreen;
